import React from "react";

import { AnalyticsEvent, sendAnalytics } from "@/utils/analytics";
import { ModalTypes } from "@/@types/common";
import { useModal } from "@/providers/modal";

import { ModalContainer } from "./style";
import { ErrorModal, PriceBreakdownModal, ProductModal } from "./ModalBody";
import { ProductInBundle } from "@/@types/data/collections";
import { KRPaymentRequiredModal } from "./ModalBody/KRPaymentRequired";
import { BundlePriceBreakdown } from "./ModalBody/PriceBreakdownModal/PriceBreakdownModal";

const Modal = ({ onCloseHandler, meta }: { onCloseHandler: CallableFunction; meta?: unknown }) => {
  const { modal, updateModal } = useModal();

  /**
   * closeModal handles closing the modal, sending analytics, and calling the onCloseHander
   * If an onCloseHandler was not provided when updating the modal, this will call a no operation function.
   */
  const closeModal = React.useCallback(() => {
    sendAnalytics(AnalyticsEvent.MODAL_ACCEPTED, { modal });
    onCloseHandler();
    updateModal();
  }, [sendAnalytics, modal, onCloseHandler, updateModal]);

  React.useEffect(() => {
    if (!modal) {
      return;
    }
    sendAnalytics(AnalyticsEvent.MODAL_OPENED, { modal });
  }, [modal]);

  if (!modal) {
    return null;
  }

  if (modal === ModalTypes.PRODUCT_PURCHASE && meta === undefined) {
    return null;
  }

  const isErrorModal = [
    ModalTypes.UNAUTHORIZED,
    ModalTypes.PAYMENT_TIMED_OUT,
    ModalTypes.PURCHASE_FAILED,
    ModalTypes.UNSUPPORTED_REGION,
    ModalTypes.SERVER_ERROR,
  ].includes(modal);

  let Modal;
  let modalClassName = `modal modal--`;
  switch (modal) {
    case ModalTypes.KR_PAYMENT_REQUIRED:
      modalClassName += "kr-payment-required-modal";
      Modal = <KRPaymentRequiredModal closeModal={closeModal} />;
      break;
    case ModalTypes.ITEM_LISTING:
      modalClassName += "item-listing-modal";
      Modal = <PriceBreakdownModal closeModal={closeModal} items={meta as BundlePriceBreakdown} />;
      break;
    case ModalTypes.PRODUCT_PURCHASE:
      modalClassName += "product-modal";
      Modal = <ProductModal closeModal={closeModal} product={meta as ProductInBundle} />;
      break;
    default:
      modalClassName += "error-modal";
      Modal = <ErrorModal closeModal={closeModal} modal={modal} />;
  }

  return (
    <ModalContainer>
      <span className="overlay" onClick={() => !isErrorModal && closeModal()} />
      <div className={modalClassName}>{Modal}</div>
    </ModalContainer>
  );
};

export default Modal;
